import { useState, useContext, useEffect } from 'react';
import {
    Card,
    CardBody,
    Button,
    Col,
    Row,
    Form,
    FormGroup,
    Label,
} from 'reactstrap';
import { useHistory, Link } from 'react-router-dom';
import { Loading } from '~/shared/components/Loading';
import { CampaignContext } from '~/context/campaign';
import { AuthContext } from '../../context/auth';
import { fetchMyCampaign } from '~/services/campaign';
import { putMyProfile } from '~/services/account';
import { useForm } from 'react-hook-form';
import { TextInput } from '~/shared/components/form/Inputs';
import toast from 'react-hot-toast';
import { renderIcon } from '~/shared/components/icons';
import { MyCampaignResponse } from '~/types/campaign';
import { AdminContext } from '~/context/admin';

export const Profile = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const [{ user }, userDispatch] = useContext(AuthContext);
    const [{ isAdmin, campaignId }, adminDispatch] = useContext(AdminContext);
    const [showEditProfile, setShowEditProfile] = useState(false);
    const history = useHistory();

    const getMyCampaign = async () => {
        try {
            const resp: MyCampaignResponse = await fetchMyCampaign();
            if (resp.detail) {
                if (resp.detail === 'Invalid token.') {
                    // Delete localStorage token
                    localStorage.removeItem('token');
                    history.push('/login');
                } else {
                    toast.error(resp.detail);
                }
            } else {
                campaignDispatch({
                    type: 'set_campaign',
                    payload: resp,
                });
                setLoading(false);
            }
        } catch (e) {
            console.log('getMyCampaign() e: ', e);
            // Show some error here
        }
    };

    useEffect(() => {
        if (!campaign) {
            getMyCampaign();
        } else {
            setLoading(false);
        }
    }, []);

    if (!campaign || loading) {
        return <Loading />;
    }

    const { person } = campaign;

    const EditContactForm = () => {
        const [savingProfile, setSavingProfile] = useState(false);
        const { first_name, last_name, phone, zip_code } = person;
        const {
            control,
            handleSubmit,
            reset,
            setValue,
            getValues,
            register,
            formState: { errors, isSubmitting },
        } = useForm({
            defaultValues: {
                first_name,
                last_name,
                phone,
                zip_code,
            },
        });
        const onSubmit = async (data) => {
            try {
                setSavingProfile(true);
                const payload = data;
                const updatedPerson = await putMyProfile(payload, campaignId);
                campaignDispatch({
                    type: 'set_person',
                    payload: updatedPerson,
                });
                setShowEditProfile(false);
                toast.success('Profile updated');
            } catch (e) {
                console.log('e: ', e);
                return;
            }
        };

        return (
            <Card className='border h-100'>
                <CardBody>
                    <h3>Edit Contact Information</h3>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <TextInput
                            name='first_name'
                            label='First Name'
                            control={control}
                            errors={errors?.first_name?.message}
                            required={true}
                            ariaRequired={true}
                        />
                        <TextInput
                            name='last_name'
                            label='Last Name'
                            control={control}
                            errors={errors?.last_name?.message}
                            required={true}
                            ariaRequired={true}
                        />
                        <TextInput
                            name='phone'
                            type='tel'
                            label='Phone Number'
                            control={control}
                            errors={errors?.phone?.message}
                            required={true}
                            ariaRequired={true}
                        />
                        <TextInput
                            name='zip_code'
                            label='Zip Code'
                            type='zip'
                            control={control}
                            errors={errors?.zip_code?.message}
                            required={true}
                            ariaRequired={true}
                        />
                        <Button
                            color='outline-dark'
                            onClick={() => setShowEditProfile(!showEditProfile)}
                        >
                            Cancel
                        </Button>
                        <Button color='primary' type='submit'>
                            Save
                        </Button>
                    </Form>
                </CardBody>
            </Card>
        );
    };

    const ContactInformation = () => {
        return (
            <Card className='border h-100'>
                <CardBody>
                    <h3 className='h4'>Contact Information</h3>
                    <div>
                        <FormGroup>
                            <div
                                className='eyebrow'
                                style={{ fontSize: '14px' }}
                            >
                                Name
                            </div>
                            {person?.first_name} {person?.last_name}
                        </FormGroup>
                        <FormGroup>
                            <div
                                className='eyebrow'
                                style={{ fontSize: '14px' }}
                            >
                                Phone Number
                            </div>
                            {person?.phone}
                        </FormGroup>
                        <FormGroup>
                            <div
                                className='eyebrow'
                                style={{ fontSize: '14px' }}
                            >
                                Zip Code
                            </div>
                            {person?.zip_code}
                        </FormGroup>
                        <Button
                            color='outline-dark'
                            size='sm'
                            onClick={() => setShowEditProfile(!showEditProfile)}
                        >
                            {renderIcon('edit_default')}
                            Edit Contact Information
                        </Button>
                    </div>
                </CardBody>
            </Card>
        );
    };

    return (
        <div className='container__inner'>
            <div className='mb-5'>
                <h1 className='h2'>My Profile</h1>
            </div>
            <Row>
                <Col lg={6} className='mb-4 mb-lg-0'>
                    {showEditProfile ? (
                        <EditContactForm />
                    ) : (
                        <ContactInformation />
                    )}
                </Col>
                <Col lg={6}>
                    <Card className='border h-100'>
                        <CardBody>
                            <h3 className='h4'>Login Information</h3>
                            <div>
                                <FormGroup>
                                    <div
                                        className='eyebrow'
                                        style={{ fontSize: '14px' }}
                                    >
                                        Email
                                    </div>
                                    {person?.email}
                                </FormGroup>
                                <FormGroup>
                                    <div
                                        className='eyebrow'
                                        style={{ fontSize: '14px' }}
                                    >
                                        Password
                                    </div>
                                    *******
                                </FormGroup>
                                <Link to='/profile/change-password'>
                                    Change Password
                                </Link>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};
